<template>
  <fwb-sidebar class="bg-black text-white pt-1 py-0">
    <div
      :class="[
        'flex flex-col bg-black text-white p-0 m-0 h-screen fixed ml-[-12px] mt-[-20px] mr-[-12px] px-0 transition-all duration-300',
        isCollapsed ? 'w-[60px]' : 'w-[15%]'
      ]"
    >
      <div
        class="flex items-center justify-between p-4"
        :class="{ 'justify-center': isCollapsed }"
      >
        <div
          class="flex items-center"
          :class="{ 'w-full justify-center': isCollapsed }"
        >
          <a
            href="/home"
            class="flex items-center bg-black text-white mb-0"
            :class="{ 'justify-center': isCollapsed }"
          >
            <img
              v-if="!isCollapsed"
              src="@/assets/imgs/LogoTuin.svg"
              class="h-6 mr-3 sm:h-7"
              alt="Tuin logo"
            >
            <img
              v-else
              src="@/assets/imgs/icons/logo_tuin_small.svg"
              alt="Tuin logo"
              width="24"
              height="37"
              class="shrink-0"
            >
          </a>
        </div>
        <button
          v-if="!isCollapsed"
          class="absolute right-2 rounded-lg transition-colors duration-200"
          @click="$emit('toggle-collapse')"
        >
          <img
            src="@/assets/imgs/icons/collapse_menu.svg"
            alt="Cerrar menú"
            width="24"
            height="24"
          >
        </button>
      </div>

      <div 
        v-if="isCollapsed"
        class="flex flex-col flex-1"
      >
        <ToolTipComponent
          placement="right"
          theme="bg-darkgray"
        >
          <template #content>
            Abrir menú
          </template>
          <div
            class="flex items-center h-10 w-10 text-center justify-center mx-2.5 my-2 text-base font-normal text-white transition duration-75 rounded-lg group hover:bg-darkgray cursor-pointer"
            @click="$emit('toggle-collapse')"
          >
            <img
              src="@/assets/imgs/icons/expand_menu.svg"
              alt="Abrir menú"
              width="24"
              height="24"
            >
          </div>
        </ToolTipComponent>

        <SidebarNavItem
          v-for="item in visibleMenuItems"
          :key="item.path"
          :item="item"
          :is-collapsed="isCollapsed"
          :is-active="currentPath === item.path"
          :show-tooltip="true"
          @navigate="changePath"
        />
        
        <div 
          class="mt-auto"
        >
          <SidebarNavItem
            :item="logoutItem"
            :is-collapsed="isCollapsed"
            :show-tooltip="true"
            @navigate="outsession"
          />
        </div>
      </div>

      <div 
        v-else 
        class="flex flex-col flex-1"
      >
        <SidebarNavItem
          v-for="item in visibleMenuItems"
          :key="item.path"
          :item="item"
          :is-collapsed="isCollapsed"
          :is-active="currentPath === item.path"
          :show-tooltip="false"
          @navigate="changePath"
        />

        <div 
          class="mt-auto"
        >
          <SidebarNavItem
            :item="logoutItem"
            :is-collapsed="isCollapsed"
            :show-tooltip="false"
            @navigate="outsession"
          />
        </div>
      </div>
    </div>
  </fwb-sidebar>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { FwbSidebar } from 'flowbite-vue';
import ToolTipComponent from "@/components/common/ToolTipComponent.vue";
import SidebarNavItem from '@/components/common/SidebarNavItem.vue';
import axios from 'axios';

import homeIcon from '@/assets/imgs/icons/home_page.svg';
import bolsaIcon from '@/assets/imgs/icons/bolsa_inmobiliaria.svg';
import propiedadesIcon from '@/assets/imgs/icons/mis_propiedades.svg';
import prospectosIcon from '@/assets/imgs/icons/prospectos.svg';
import inmobiliariaIcon from '@/assets/imgs/icons/mi_inmobiliaria.svg';
import perfilIcon from '@/assets/imgs/icons/mi_perfil.svg';
import pagosIcon from '@/assets/imgs/icons/pagos_facturacion.svg';
import logoutIcon from '@/assets/imgs/icons/logout.svg';

const store = useStore();
const route = useRoute();

defineEmits(['toggle-collapse']);

defineProps({
  isCollapsed: {
    type: Boolean,
    default: false
  }
});

const currentPath = ref(route.path);
const subscriptionStatus = ref('active');
let subscriptionInterval = null;

const session = computed(() => store.getters.getUserSession);
const stepUser = computed(() => store.getters.getUserSession);

const menuItems = [
  { key: 'home',              path: '/home',              label: 'Home',                icon: homeIcon,        alt: 'tuin home page',        minStep: 3 },
  { key: 'bolsa-inmobiliaria',path: '/bolsa-inmobiliaria',label: 'Bolsa inmobiliaria',  icon: bolsaIcon,       alt: 'bolsa inmobiliaria',    minStep: 3 },
  { key: 'properties',        path: '/properties',        label: 'Mis propiedades',     icon: propiedadesIcon, alt: 'mis propiedades',       minStep: 3 },
  { key: 'customers',         path: '/customers',         label: 'Prospectos',          icon: prospectosIcon,  alt: 'prospectos',            minStep: 3 },
  { key: 'info-company',      path: '/info-company',      label: 'Mi inmobiliaria',     icon: inmobiliariaIcon, alt: 'mi inmobiliaria',       minStep: 3 },
  { key: 'info-profile',      path: '/info-profile',      label: 'Mi perfil',           icon: perfilIcon,      alt: 'mi perfil',             minStep: 3 },
  { key: 'info-payments',     path: '/info-payments',     label: 'Pagos y facturación', icon: pagosIcon,       alt: 'pagos y facturación',   minStep: 3 },
];

const logoutItem = {
  path: null,
  label: 'Salir',
  icon: logoutIcon,
  alt: 'cerrar sesión'
};

const visibleMenuItems = computed(() => {
  const step = stepUser.value?.step ?? 0;
  return menuItems.filter(item => step >= item.minStep);
});

function changePath(pathLink) {
  currentPath.value = pathLink;
}

watch(() => route.path, (newPath) => {
  currentPath.value = newPath;
});

async function outsession() {
  await store.dispatch('clearUserSession');
  window.location.replace('/login');
}

async function checkSubscription() {
  try {
    const options = {
      headers: {
        Authorization: `Bearer ${session.value.token_api}`,
      },
    };

    const response = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/broker/checkActive/${session.value.id}`,
      options
    );

    const data = response.data?.original;

    if (data) {
      const { status, plan_id } = data;
      subscriptionStatus.value = (status !== 'active' || plan_id === 9) ? 'expired' : 'active';
    } else {
      subscriptionStatus.value = 'expired';
    }
  } catch {
    subscriptionStatus.value = 'expired';
  }
}

onMounted(async () => {
  await checkSubscription();
  subscriptionInterval = setInterval(checkSubscription, 300000);
});

onUnmounted(() => {
  if (subscriptionInterval) {
    clearInterval(subscriptionInterval);
  }
});
</script>

<style scoped>
aside > div:first-child {
  background-color: black !important;
  color: white;
}
</style>